import { useCallback, useRef } from 'react';

const DEFAULT_THRESHOLD = 60;
const MIN_HORIZONTAL_RATIO = 1.5; // horizontal movement must be 1.5x vertical to count as swipe

type SwipeDirection = 'left' | 'right';

/**
 * Returns touch handlers for swipe-to-close gesture.
 * - direction 'right': panel on left (e.g. profile) — user swipes right to close
 * - direction 'left': panel on right (e.g. backlog) — user swipes left to close
 */
export function useSwipeToClose(
  onClose: () => void,
  direction: SwipeDirection,
  options: { threshold?: number } = {}
) {
  const threshold = options.threshold ?? DEFAULT_THRESHOLD;
  const start = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) start.current = { x: t.clientX, y: t.clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!start.current) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const deltaX = t.clientX - start.current.x;
      const deltaY = t.clientY - start.current.y;
      start.current = null;

      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < absY * MIN_HORIZONTAL_RATIO) return; // more vertical than horizontal, don't close

      if (direction === 'right' && deltaX > threshold) {
        onClose();
      } else if (direction === 'left' && deltaX < -threshold) {
        onClose();
      }
    },
    [direction, threshold, onClose]
  );

  return { onTouchStart, onTouchEnd };
}
